/**
 * Submission Portal — the list of what you're building.
 *
 * ⚠️ Entirely separate from COMM-PASS. Nothing here reads or writes
 * public.submissions; these live in portal_submissions and the live queue is
 * unaffected by anything on this screen.
 *
 * The grade is on every row on purpose. An agent with four half-built
 * submissions needs to know which one is nearly ready and which is a shell, and
 * that is exactly what the letter answers at a glance.
 *
 * ⚠️ THE AVERAGE GRADE CARD USES letterFor FROM @commpass/shared, NOT ITS OWN
 * CUTOFFS. A second copy of the A+…F table here would eventually disagree with
 * the rows underneath it, and a member reading "B" on the card above a column
 * of B-minuses would be right to distrust both numbers.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MIN_SEND_SCORE, letterFor } from '@commpass/shared';
import { PageHero } from '../components/PageHero';
import { StatCard } from '../components/StatCard';
import { ApiError, api, type PortalSubmissionSummary } from '../lib/api';

type StatusFilter = 'all' | 'drafting' | 'sent' | 'withdrawn';
type Sort = 'updated' | 'grade' | 'name';

const STATUS_LABEL: Record<PortalSubmissionSummary['status'], string> = {
  drafting: 'Draft',
  sent: 'Submitted',
  withdrawn: 'Withdrawn',
};

/** The tag modifier per status — named for the state, never for the colour. */
const STATUS_TAG: Record<PortalSubmissionSummary['status'], string> = {
  drafting: 'is-draft',
  sent: 'is-submitted',
  withdrawn: 'is-withdrawn',
};

/** What the letter means, in words, beside it. */
function gradeWords(band: string): string {
  if (band === 'a') return 'Excellent';
  if (band === 'b') return 'Good submission';
  if (band === 'c') return 'Some gaps';
  return 'Needs work';
}

const shortDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

/** First letter of the business name, or U for one that has not been named yet. */
const initialOf = (name: string | null): string => (name?.trim()?.[0] ?? 'U').toUpperCase();

export function SubmissionPortal() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<PortalSubmissionSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  const [q, setQ] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [band, setBand] = useState('all');
  const [sort, setSort] = useState<Sort>('updated');

  useEffect(() => {
    api
      .portalList()
      .then((r) => setRows(r.submissions))
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Could not load your submissions.'));
  }, []);

  const start = async () => {
    setStarting(true);
    try {
      const { id } = await api.portalCreate({});
      navigate(`/submission/${id}`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not start a submission.');
      setStarting(false);
    }
  };

  /** Null until the fetch answers — the cards show a placeholder, not a zero. */
  const loading = rows === null;
  // Memoised only so the two useMemos below don't see a fresh [] every render.
  const all = useMemo(() => rows ?? [], [rows]);

  /**
   * ⚠️ "NEEDS ATTENTION" IS A REAL THRESHOLD, NOT A FEELING. It counts drafts
   * scoring below MIN_SEND_SCORE — the same line the builder uses to decide
   * whether a submission may be sent at all. Any looser definition (say, "graded
   * C or worse") would be a number invented on this page that nothing else in
   * the app agrees with.
   */
  const stats = useMemo(() => {
    const drafts = all.filter((r) => r.status === 'drafting');
    const scores = all.map((r) => r.grade.score);
    return {
      total: all.length,
      drafts: drafts.length,
      attention: drafts.filter((r) => r.grade.score < MIN_SEND_SCORE).length,
      average: scores.length ? letterFor(Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)) : null,
    };
  }, [all]);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const out = all.filter(
      (r) =>
        (status === 'all' || r.status === status) &&
        (band === 'all' || r.grade.band === band) &&
        (!needle ||
          `${r.business_name ?? ''} ${r.class_name ?? ''} ${r.id}`.toLowerCase().includes(needle)),
    );
    return out.sort((a, b) => {
      if (sort === 'grade') return b.grade.score - a.grade.score;
      if (sort === 'name') return (a.business_name ?? 'zzz').localeCompare(b.business_name ?? 'zzz');
      return b.updated_at.localeCompare(a.updated_at);
    });
  }, [all, q, status, band, sort]);

  const filtered = q.trim() !== '' || status !== 'all' || band !== 'all';

  return (
    <div className="wrap">
      <PageHero
        title="Submission Portal"
        sub="Build a submission yourself and see how it grades before it goes out."
        action={
          <button className="btn btn-primary" disabled={starting} onClick={() => void start()}>
            {starting ? 'Starting…' : '+ Start a submission'}
          </button>
        }
      />

      {error && <div className="empty">{error}</div>}

      <div className="stat-row">
        <StatCard
          tone="blue"
          icon="M6 3h9l4 4v14H6zM14 3v5h5M9 12h7M9 16h5"
          label="Total submissions"
          value={stats.total}
          note="All submissions"
          loading={loading}
        />
        <StatCard
          tone="violet"
          icon="M4 20h4l10-10-4-4L4 16zM14 6l4 4"
          label="Drafts"
          value={stats.drafts}
          note="Not yet submitted"
          loading={loading}
        />
        <StatCard
          tone="amber"
          icon="M12 4l9 16H3zM12 10v4M12 17h.01"
          label="Needs attention"
          value={stats.attention}
          note="Below the score needed to send"
          loading={loading}
          slow={stats.attention > 0}
        />
        <StatCard
          tone="green"
          icon="M12 3a5 5 0 100 10 5 5 0 000-10zM9 13l-1 8 4-2 4 2-1-8"
          label="Average grade"
          value={stats.average ?? '—'}
          note={stats.average ? 'Across all submissions' : 'Nothing graded yet'}
          loading={loading}
        />
      </div>

      <section className="panel lr-table-panel">
        <div className="lr-toolbar">
          <div className="lr-search">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M11 4a7 7 0 100 14 7 7 0 000-14zM20 20l-4-4" />
            </svg>
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search submissions…"
              aria-label="Search submissions"
            />
          </div>

          <div className="lr-filters">
            <select value={status} onChange={(e) => setStatus(e.target.value as StatusFilter)} aria-label="Status">
              <option value="all">All statuses</option>
              <option value="drafting">Draft</option>
              <option value="sent">Submitted</option>
              <option value="withdrawn">Withdrawn</option>
            </select>
            <select value={band} onChange={(e) => setBand(e.target.value)} aria-label="Grade">
              <option value="all">All grades</option>
              <option value="a">A</option>
              <option value="b">B</option>
              <option value="c">C</option>
              <option value="d">D</option>
              <option value="f">F</option>
            </select>
            <select value={sort} onChange={(e) => setSort(e.target.value as Sort)} aria-label="Sort by">
              <option value="updated">Sort by last updated</option>
              <option value="grade">Sort by grade</option>
              <option value="name">Sort by name</option>
            </select>
          </div>
        </div>

        {!rows ? (
          <p className="lr-empty">Loading…</p>
        ) : !visible.length ? (
          <p className="lr-empty">
            {all.length ? 'Nothing matches that.' : 'Nothing in progress.'}
            <span>
              {all.length
                ? 'Try another status, or clear the search.'
                : 'Start one here, or ask Connor about a risk and let him open it for you with the class already worked out.'}
            </span>
          </p>
        ) : (
          <>
            <table className="lr-table">
              <thead>
                <tr>
                  <th>Submission</th>
                  <th>Class / details</th>
                  <th>Grade</th>
                  <th>Status</th>
                  <th>Updated</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => navigate(`/submission/${r.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <div className="lr-cell">
                        <span className={`grade-badge is-${r.grade.band}`} aria-hidden="true">
                          {initialOf(r.business_name)}
                        </span>
                        <div>
                          <b>{r.business_name || 'Untitled submission'}</b>
                          <span>{r.id}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <b style={{ fontWeight: 600 }}>{r.class_name || '—'}</b>
                      {/* The gaps are the "details": what is still missing is more
                          use on a list than a second copy of the class name. */}
                      <div className="lr-sub">
                        {r.status === 'sent' ? 'Sent' : r.grade.gaps.slice(0, 2).join(' · ') || 'Ready to send'}
                      </div>
                    </td>
                    <td>
                      <div className="grade-cell">
                        <span className={`grade-badge is-${r.grade.band}`}>{r.grade.grade}</span>
                        <span>{gradeWords(r.grade.band)}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`tag ${STATUS_TAG[r.status]}`}>{STATUS_LABEL[r.status]}</span>
                    </td>
                    <td className="sp-when">
                      <b>{shortDate(r.sent_at ?? r.updated_at)}</b>
                      {/* Honest because portalList only ever returns the caller's
                          own submissions — there is no other author it could be. */}
                      <span>by you</span>
                    </td>
                    <td className="lr-chev">
                      <button type="button" aria-label={`Open ${r.business_name || 'submission'}`}>
                        <svg viewBox="0 0 24 24">
                          <path d="M9 6l6 6-6 6" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <p className="lr-count-line">
              Showing {visible.length} of {all.length} submission{all.length === 1 ? '' : 's'}
              {filtered ? ' (filtered)' : ''}
            </p>
          </>
        )}
      </section>
    </div>
  );
}
